import React from 'react';
import _has from 'lodash.has';
import { satelliteTrack } from '@dc/data-layer/src/utils.js';
import { resolveByCountry } from '@dc/ui-library/src/helpers/environment';
import {
  getCurrentPaginatorPage, createMarkup,
  getCurrentFiltersSetup, getCurrentUCMS
} from '../../helpers';
import browserHistory from '../../../../../appState/browserHistory';
import { setPriceRangeTo } from '../../../../../appState/ducks/listingPageFilters';

export const filterTypes = {
  rating: 'rating',
  location: 'location',
  base: 'base',
  price: 'price'
};

export function preloadImage(url) {
  const preloadedImage = new Image();
  preloadedImage.src = url;
  return preloadedImage;
}

export function touchDeviceType(deviceType) {
  if (deviceType === 'tablet' || deviceType === 'mobile') {
    return true;
  }
  return false;
}

export function getActiveFiltersCount() {
  let setPriceRange = false;

  return window.appState.getState().listingPage.filters.list.reduce(
    (totalActiveCount, filter) => {
      if (filter.name === filterTypes.price && !setPriceRange) {
        for (const course in filter.selectedBetween) {
          if (filter.selectedBetween[course].value > 0) {
            setPriceRange = true;
            return totalActiveCount + 1;
          }
        }
      }
      return totalActiveCount + filter.options.reduce(
        (filterActiveCount, filterOption, optionCounter) => {
          if (filterOption.active) {
            return filterActiveCount + 1;
          } if (filterOption.active && filter.name === filterTypes.price &&
            optionCounter === 0) {
            return filterActiveCount + 1;
          }
          return filterActiveCount;
        }, 0
      );
    }, 0
  );
}

export function checkedOptionsFilters() {
  const state = window.appState && window.appState.getState();
  const isFiltersListAvailable = _has(state, 'listingPage.filters.list');
  if (isFiltersListAvailable) {
    return state.listingPage.filters.list.filter(
      (filterItem) => filterItem && filterItem.options.find((itemOption) => itemOption.active));
  }
  return [];
}

export function getSpecificActiveFilter(theFilter = '') {
  const activeFilters = checkedOptionsFilters();
  if (activeFilters.length > 0) {
    return activeFilters.reduce((accumulator, specificFilter) => {
      if (specificFilter.label === theFilter) {
        accumulator.push(specificFilter);
      }
      return accumulator;
    }, []);
  }
  return false;
}

export function isFilterActive(filter) {
  return filter.name === filterTypes.price
    ? (filter.currentFormBetween.right.value !== '' ||
    filter.currentFormBetween.left.value !== '') &&
    isPriceRangeValid(
      {
        type: filter.type,
        between: filter.selectedBetween
      }
    )
    : !!filter.options.find((option) => option.active === true);
}

export function hasMultipleActiveOptions(filter) {
  return filter && filter.options && filter.options
    .filter((option) => option.active === true).length > 1;
}

export function hasActiveFilter(propsData, selectedFilter = false) {
  if (selectedFilter) {
    return propsData.filters.find((filterItem) => filterItem.name === selectedFilter && isFilterActive(filterItem));
  }
  return !!(propsData.filters.find((filterItem) => isFilterActive(filterItem)));
}

export function getCurrentBrandFilters() {
  const brandFilter = window.appState.getState().listingPage.filters.list.find(
    (filter) => filter.name === 'brand_id'
  );

  if (!brandFilter) {
    return [];
  }

  return brandFilter.options
    .filter((option) => option.active)
    .map((option) => parseInt(option.value, 10));
}

export function getCurrentPriceMinFilter() {
  const priceFilter = window.appState.getState().listingPage.filters.list.find(
    (filter) => filter.type === filterTypes.price
  );

  if (!priceFilter) {
    return null;
  }

  if (priceFilter.currentFormBetween.left.value === '') {
    return null;
  }

  return priceFilter.currentFormBetween.left.value;
}

export function getCurrentPriceMaxFilter() {
  const priceFilter = window.appState.getState().listingPage.filters.list.find(
    (filter) => filter.type === filterTypes.price
  );

  if (!priceFilter || priceFilter.currentFormBetween.right.value === '') {
    return null;
  }

  return priceFilter.currentFormBetween.right.value;
}

/**
 * Rating filter has own method for generating XHR POST data. Example reason:
 * When user clicks on 3 stars checkbox, we need to send all checkboxes above these 3 stars.
 * So it's needed to append 4 stars and 5 stars into XHR POST request too.
 * API works with these values as exact values, not like "3 or more" stars, but "exactly 3 stars".
 * @returns {Array}
 */
function getRatingOptionsForXHR(filter) {
  const activeOption = filter.options.find((filterOption) => filterOption.active);
  if (!activeOption) {
    return [];
  }
  return filter.options.filter(
    (filterOption) => parseInt(filterOption.value, 10) >= parseInt(activeOption.value, 10)
  );
}

/**
 * Returns object containing active filters used in POST XHR call.
 * Object key is filter name (not label), value is array of active filter option values.
 * Example: {"ba00009976":["bv00307777"], "brand_id": ["2990", "21956"]}
 * @returns {{}}
 */
export function getCurrentFiltersForXHR() {
  return window.appState.getState().listingPage.filters.list.reduce(
    (accumulator, currFilter) => {
      let activeOptions;
      if (currFilter.type === filterTypes.rating) {
        activeOptions = getRatingOptionsForXHR(currFilter);
      } else {
        activeOptions = currFilter.options.filter((filterOption) => filterOption.active);
      }

      if (activeOptions.length === 0 ||
        currFilter.name === 'brand_id' || // Brand has it's own POST field
        currFilter.type === filterTypes.price) { // Price checkboxes are not sent in POST XHR
        return accumulator;
      }
      return {
        ...accumulator,
        [currFilter.name]: activeOptions.map((activeOption) => activeOption.value)
      };
    },
    {}
  );
}

export function buildListingPageXhrPostBody() {
  const state = window.appState.getState().listingPage;
  const [
    sortField, sortDirection
  ] = state.pageSetup.sortByOptions.selectedOptionValue.split('/')[0].split('-');

  return {
    sortDirection: `${sortDirection}ending`,
    sortField: sortField === 'relevance' ? 'none' : sortField,
    productCount: state.pagination.itemsNumberPerPage.selectedOptionValue,
    pageNumber: getCurrentPaginatorPage() ? getCurrentPaginatorPage().label : '1',
    marketId: state.pageSetup.category.marketId,
    segmentId: state.pageSetup.category.segmentId,
    categoryId: state.pageSetup.category.categoryId,
    brandIds: getCurrentBrandFilters(),
    priceMin: getCurrentPriceMinFilter(),
    priceMax: getCurrentPriceMaxFilter(),
    attributes: getCurrentFiltersForXHR(),
    view: state.pageSetup.viewSwitch
  };
}

export function transformForActiveFilters(filters) {
  return filters.map((filter) => {
    if (filter.type === filterTypes.price) {
      if (isPriceRangeValid({
        type: filter.type,
        between: filter.selectedBetween
      }) && getPriceRangeLabel(filter.selectedBetween) !== false) {
        return {
          ...filter,
          options: [
            {
              label: getPriceRangeLabel(filter.selectedBetween),
              value: null,
              active: true
            }
          ]
        };
      }
      return {
        ...filter,
        options: []
      };
    }
    return filter;
  });
}

export function isPriceRangeValid(filter) {
  if (filter.type !== filterTypes.price) {
    return false;
  }
  const leftValue = filter.between.left.value;
  const rightValue = filter.between.right.value;

  if (leftValue !== '' && rightValue !== '' && leftValue > rightValue) {
    return false;
  }

  return true;
}

export function getPriceRangeLabel(between) {
  if (!between || (between.left.value === '' && between.right.value === '')) {
    return false;
  }

  if (between.left.value === '' || between.left.value === 0) {
    return (
      <span
        dangerouslySetInnerHTML={
        createMarkup(
          `Under ${between.right.currency.symbol}` +
          `${between.right.value}`
        )
      }
      />
    );
  }

  if (between.right.value === '' || between.right.value === 0) {
    return (
      <span
        dangerouslySetInnerHTML={
        createMarkup(
          `Over ${between.left.currency.symbol}` +
          `${between.left.value}`
        )
      }
      />
    );
  }

  return (
    <span
      dangerouslySetInnerHTML={
      createMarkup(
        `${between.left.currency.symbol}${between.left.value} -
         ${between.right.currency.symbol}${between.right.value}`
      )
    }
    />
  );
}

/**
 * Make some data changes after receiving POST XHR response to better fit to app state.
 * @param filtersList
 * @param firstPageLoad
 * @returns {Array|*}
 */
export function prepareFiltersPayload(filtersList, firstPageLoad = false) {
  return filtersList.map((filter) => {
    if (filter.type === filterTypes.price) {
      let newFilter = { ...filter };

      // if only one product due to selected filters occurs
      // set "to" price to rounded price of such product
      const activeStoredResponse = window.appState.getState()
        .listingPage.products.storedResponses.find((response) => response.isActive === true);
      const productList = activeStoredResponse && activeStoredResponse.data.productList;
      let firstProductPrice = productList && productList.length === 1 &&
          productList[0].price.price.split(resolveByCountry('£', '€'))[1]
            .replace(resolveByCountry(',', '.'), '');
      if (!productList) {
        firstProductPrice = window.digitalData.products &&
          window.digitalData.products[0].currentPrice;
      }
      const roundedPrice = firstProductPrice ? Math.round(firstProductPrice) : '';

      if (firstPageLoad || !filter.selectedBetween.left) {
        newFilter = {
          ...newFilter,
          selectedBetween:
            {
              ...newFilter.selectedBetween,
              left: {
                ...newFilter.limitBetween.left,
                printStyle: '',
                value: ''
              }
            }
        };
      }

      if (firstPageLoad || !filter.selectedBetween.right) {
        newFilter = {
          ...newFilter,
          selectedBetween:
            {
              ...newFilter.selectedBetween,
              right: {
                ...newFilter.limitBetween.right,
                printStyle: '',
                value: ''
              }
            }
        };
      }

      // Create currentFormBetween object and set empty values there
      newFilter.currentFormBetween = {
        ...newFilter.limitBetween
      };

      if (firstPageLoad && (!newFilter.selectedBetween.left || !newFilter.selectedBetween.right)) {
        newFilter.currentFormBetween = newFilter.limitBetween;
      } else if (firstPageLoad && filter.selectedBetween.left && filter.selectedBetween.right) {
        newFilter.currentFormBetween = filter.selectedBetween;
        newFilter.selectedBetween = filter.selectedBetween;
      } else {
        newFilter.currentFormBetween = newFilter.selectedBetween;
      }

      if ((productList && productList.length === 1) ||
        window.digitalData.products.length === 1) {
        newFilter = {
          ...newFilter,
          selectedBetween:
            {
              ...newFilter.selectedBetween,
              right: {
                ...newFilter.limitBetween.right,
                printStyle: roundedPrice.toString(),
                value: roundedPrice
              }
            },
          currentFormBetween:
            {
              ...newFilter.currentFormBetween,
              right: {
                ...newFilter.limitBetween.right,
                printStyle: roundedPrice.toString(),
                value: roundedPrice
              }
            }
        };
      }

      // PHP is returning null in active prop, transforming to false value.
      let newFilterOptions = newFilter.options.map(
        (filterOption) => ({ ...filterOption, active: false })
      );

      // PHP is not returning checked options in price filters so we need to check them again.
      // Find values of currently selected checkboxes in price filter
      const reduxFilter = window.appState.getState().listingPage.filters.list.find(
        (storedFilter) => storedFilter.name === newFilter.name
      );

      // Activate previously (before reload) checked priceRange options
      // when that occurs in activeFilters
      if (firstPageLoad) {
        if (newFilter.selectedBetween &&
          (newFilter.selectedBetween.left && newFilter.selectedBetween.right)) {
          newFilterOptions = newFilterOptions.map((option) => {
            if (option.value === (`${newFilter.selectedBetween.left.value
            }-${newFilter.selectedBetween.right.value}`)) {
              return { ...option, active: true };
            } if (newFilter.selectedBetween.right.value ===
              parseInt(option.value.split('-')[1], 10)) {
              return { ...option, active: true };
            } if (newFilter.selectedBetween.left.value ===
              parseInt(option.value.split('-')[0], 10)) {
              return { ...option, active: true };
            }
            return option;
          });
        }
      }

      if (reduxFilter) {
        // Get only values of active price options
        const currentlyCheckedOptions = reduxFilter.options
          .filter((option) => option.active)
          .map((option) => option.value);

        // map these selected checbkoxes to returned response and make them active
        newFilterOptions = newFilterOptions.map((option) => {
          if (currentlyCheckedOptions.indexOf(option.value) !== -1) {
            return { ...option, active: true };
          }
          return option;
        });
      }

      return {
        ...newFilter,
        options: newFilterOptions
      };
    }
    if (filter.type === filterTypes.rating) {
      // When user selects 3 stars, POST request is sent with 3,4 and 5 stars.
      // Because 3 stars means "3 and more". In this case PHP responds back also with 3,4 a 5
      // stars as active. But UI allows to have active only one rating checkbox
      // So it's needed to find the lowest value checkbox and keep active only that one
      // All other checkboxes are marked as inactive (unchecked).

      const lowestActiveValue =
        Math.min(...filter.options.filter((option) => option.active)
          .map((option) => parseInt(option.value, 10)));

      return {
        ...filter,
        options: filter.options.map((option) => (
          parseInt(option.value, 10) !== lowestActiveValue
            ? {
              ...option,
              active: false
            }
            : option
        ))
      };
    }
    return filter;
  });
}

export function setDataLayerRefinement() {
  const listingAppState = window.appState.getState().listingPage;
  const refinementObject = listingAppState.filters.list.reduce(
    (accumulator, currFilter) => {
      let activeOptions = currFilter.options.filter((filterOption) => filterOption.active)
        .map((activeOption) => activeOption.label)
        .join('|');

      if (activeOptions.length === 0) {
        if (currFilter.type === filterTypes.price) {
          activeOptions = getDataLayerPriceBetweenRefinement(currFilter);
          if (!activeOptions) {
            return accumulator;
          }
        } else {
          return accumulator;
        }
      }

      return [
        ...accumulator,
        {
          searchRefinementAttribute: currFilter.label,
          searchRefinementValue: activeOptions
        }
      ];
    },
    []
  );

  refinementObject.push({
    searchRefinementAttribute: 'Product Category',
    searchRefinementValue: listingAppState.pageSetup.category.primaryCategory
  });

  window.appState.dispatch(
    {
      type: 'SET_DATALAYER_FILTERS_SETUP',
      payload: refinementObject
    }
  );
  satelliteTrack('refinementUpdate');
}

function getDataLayerPriceBetweenRefinement(filter) {
  const between = filter.currentFormBetween;

  if (between.left.value === '' && between.right.value === '') {
    return false;
  }

  if (between.left.value === '' || between.left.value === 0) {
    return `under ${between.right.value}`;
  }

  if (between.right.value === '' || between.right.value === 0) {
    return `over ${between.left.value}`;
  }

  return `${between.left.value}-${between.right.value}`;

}

export function pushBrowserHistoryCurrentFilters() {
  return browserHistory.pushState({
    type: browserHistory.actionTypes.LISTING_PAGE_FILTERS,
    payload: getCurrentFiltersSetup()
  }, getCurrentUCMS().segment, getCurrentPaginatorPage().url);
}

export function validateFilters() {
  window.appState.getState().listingPage.filters.list.forEach((filter) => {
    if (filter.type === filterTypes.price) {
      if (filter.currentFormBetween.left.value !== '' &&
          filter.currentFormBetween.right.value !== '' &&
          parseInt(filter.currentFormBetween.left.value, 10) >
          parseInt(filter.currentFormBetween.right.value, 10)) {
        window.appState.dispatch(setPriceRangeTo({
          filterName: filter.name,
          value: parseInt(filter.currentFormBetween.left.value, 10)
        }));
      }
    }
  });
}
